/* eslint-disable */
import React, { useState, useRef } from "react";
import { supabase } from "./supabase";
import styles from "./components/EntryLoginScreen.module.css";
import { PasswordStrengthMeter } from "./components/auth/PasswordStrengthMeter";

// ═══════════════════════════════════════════════════════════════════════════════
// Login Screen — Sandstone Luxury Design
// NOTE: The `LoginScreen` export below is dead code as of 2026-05-15 (T-200).
// App.js:149 routes unauthenticated users to EntryLoginScreen, not this. The
// `SetPasswordScreen` export at the bottom of this file IS live and was
// redesigned in T-200 to match the new DANA Modern Operations system.
// ═══════════════════════════════════════════════════════════════════════════════

const inputStyle = {
  width: "100%", padding: "var(--space-lg) var(--space-xl)", border:"var(--border-thin) solid var(--border)",
  background: "#f7f7f5", fontFamily: "'Montserrat',system-ui,sans-serif",
  fontSize: 'var(--text-xs)', color: "var(--text-soft)", borderRadius:'var(--radius-xs)', boxSizing: "border-box",
  outline: "none", transition: "border-color 0.3s ease",
};

const placeholderCSS = `
  .clb-login-input::placeholder {
    color: var(--text-label); text-transform: uppercase; font-size: 9px; letter-spacing: 3px;
  }
  .clb-login-input:focus { border-color: var(--gold) !important; }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }
  @keyframes errorFadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

export function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);
  const [mode, setMode] = useState("login");
  const [resetMsg, setResetMsg] = useState("");
  const cardRef = useRef(null);

  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 300);
  };

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter email and password"); triggerShake(); return; }
    setLoading(true); setError("");
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) { setError("Wrong email or password"); setLoading(false); triggerShake(); return; }
    onLogin(data.user);
  };

  const handleForgotPassword = async () => {
    if (!email) { setError("Enter your email first"); triggerShake(); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://carolina-lemke-catalog-ujup.vercel.app",
    });
    setLoading(false);
    if (err) { setError(err.message); triggerShake(); return; }
    setMode("reset_sent");
    setResetMsg(`Password reset link sent to ${email}. Check your inbox.`);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f5", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Montserrat',system-ui,sans-serif" }}>
      <style>{placeholderCSS}</style>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 460, padding: "0 var(--space-2xl)"}}>
        {/* Logo — above card */}
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <span style={{ fontSize: 'var(--text-base)', fontWeight: 300, letterSpacing: 9, textTransform: "uppercase", color: "#2e2820", whiteSpace: "nowrap", display: "block" }}>Carolina Lemke</span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 'var(--space-md)', margin: "var(--space-sm) 0 var(--space-sm)"}}>
            <div style={{ width: 36, height: "0.5px", background: "#c8a060" }}/>
            <div style={{ width: 6, height: 6, background: "#c8a060", transform: "rotate(45deg)", flexShrink: 0 }}/>
            <div style={{ width: 36, height: "0.5px", background: "#c8a060" }}/>
          </div>
          <span style={{ fontSize: 'var(--text-2xs)', fontWeight: 300, letterSpacing: 6, textTransform: "uppercase", color: "var(--sandstone)", display: "block" }}>Berlin</span>
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 82, letterSpacing: 18, color: "#1e1a14", fontWeight: 200, textTransform: "uppercase", lineHeight: .9, display: "block", marginTop: 'var(--space-md)'}}>Dana</span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 'var(--space-md)', marginTop: 'var(--space-md)'}}>
            <div style={{ width: 24, height: "0.5px", background: "#c8a060" }}/>
            <span style={{ fontSize: 'var(--text-2xs)', letterSpacing: 5, color: "#c8a060", fontWeight: 'var(--weight-medium)', textTransform: "uppercase" }}>Operations Hub</span>
            <div style={{ width: 24, height: "0.5px", background: "#c8a060" }}/>
          </div>
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          style={{
            width: "100%", background: "var(--card-bg)", border:"var(--border-thin) solid var(--border)",
            padding: "36px 42px 42px", textAlign: "center", position: "relative", overflow: "hidden",
            animation: shaking ? "shake 0.3s ease" : "none",
          }}
        >
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent 10%,#d4ac6c 50%,transparent 90%)" }}/>

        {mode === "reset_sent" && (
          <div>
            <div style={{ fontSize: 'var(--text-sm)', color: "var(--text-soft)", fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-sm)', letterSpacing: "0.5px" }}>{resetMsg}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: "var(--text-label)", marginBottom: 'var(--space-2xl)'}}>Click the link in the email to set a new password.</div>
            <button onClick={() => { setMode("login"); setResetMsg(""); setError(""); }}
              style={{
                width: "100%", padding: 'var(--space-lg)', background: "transparent", color: "var(--sandstone)",
                border:"var(--border-thin) solid var(--border)", borderRadius:'var(--radius-xs)', fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-xs)',
                cursor: "pointer", letterSpacing: "4px", textTransform: "uppercase",
                fontFamily: "'Montserrat',system-ui,sans-serif",
              }}>
              BACK TO SIGN IN
            </button>
          </div>
        )}

        {mode !== "reset_sent" && (<>
          <div style={{ marginBottom: 'var(--space-lg)'}}>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="EMAIL" className="clb-login-input"
              style={inputStyle}
              onKeyDown={e => e.key === "Enter" && (mode === "login" ? handleLogin() : handleForgotPassword())} />
          </div>

          {mode === "login" && (
            <div style={{ marginBottom: 'var(--space-lg)'}}>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="PASSWORD" className="clb-login-input"
                style={inputStyle}
                onKeyDown={e => e.key === "Enter" && handleLogin()} />
            </div>
          )}

          {error && (
            <div style={{ fontSize: 'var(--text-xs)', color: "#c4836a", marginBottom: 'var(--space-lg)', animation: "errorFadeIn 0.3s ease" }}>
              {error}
            </div>
          )}

          {mode === "login" && (
            <button onClick={handleLogin} disabled={loading}
              style={{
                width: "100%", padding: 'var(--space-lg)', background: "var(--text-soft)", color: "var(--card-bg)",
                border: "none", borderRadius:'var(--radius-xs)', marginTop: 'var(--space-sm)', fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-xs)',
                cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.85 : 1,
                letterSpacing: "4px", textTransform: "uppercase", transition: "opacity 0.15s",
                fontFamily: "'Montserrat',system-ui,sans-serif",
              }}>
              {loading ? "SIGNING IN..." : "SIGN IN"}
            </button>
          )}

          {mode === "forgot" && (
            <button onClick={handleForgotPassword} disabled={loading}
              style={{
                width: "100%", padding: 'var(--space-lg)', background: "var(--text-soft)", color: "var(--card-bg)",
                border: "none", borderRadius:'var(--radius-xs)', marginTop: 'var(--space-sm)', fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-xs)',
                cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.85 : 1,
                letterSpacing: "4px", textTransform: "uppercase", transition: "opacity 0.15s",
                fontFamily: "'Montserrat',system-ui,sans-serif",
              }}>
              {loading ? "SENDING..." : "SEND RESET LINK"}
            </button>
          )}

          <button onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setError(""); }}
            style={{
              width: "100%", marginTop: 'var(--space-lg)', padding: 'var(--space-md)', background: "transparent",
              color: "var(--text-label)", border: "none", cursor: "pointer", fontSize: 'var(--text-2xs)',
              letterSpacing: "2px", textTransform: "uppercase",
              fontFamily: "'Montserrat',system-ui,sans-serif",
            }}>
            {mode === "login" ? "FORGOT PASSWORD?" : "BACK TO SIGN IN"}
          </button>
        </>)}

        {/* Footer */}
        <div style={{ marginTop: 'var(--space-4xl)', fontSize: 'var(--text-2xs)', color: "var(--text-label)", letterSpacing: "2px", textTransform: "uppercase" }}>
          LOKA &middot; INTERNAL SYSTEM
        </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Set New Password screen (used for reset + forced change)
// T-200 — Redesigned in DANA Modern Operations system. Auth handler signature
// + props (title, subtitle, onDone) preserved verbatim from prior design.
// Mounted by App.js:147-148 for PASSWORD_RECOVERY auth event + mustChangePassword.
// Imports the same module CSS as EntryLoginScreen for DANA brand consistency.
// ═══════════════════════════════════════════════════════════════════════════════

function SetPasswordCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12 L10 18 L20 6"/>
    </svg>
  );
}

function SetPasswordDiamondMark() {
  return (
    <svg className={styles.brandMark} viewBox="0 0 32 32" width="36" height="36" aria-hidden="true">
      <path d="M16 1.5 L30.5 16 L16 30.5 L1.5 16 Z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
      <path d="M16 9 L23 16 L16 23 L9 16 Z" fill="currentColor"/>
    </svg>
  );
}

export function SetPasswordScreen({ title, subtitle, onDone }) {
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [pwHasError, setPwHasError] = useState(false);
  const [confirmHasError, setConfirmHasError] = useState(false);
  // Live strength verdict (PasswordStrengthMeter) — mirrors the server policy
  // (min 8 + HIBP leaked-password protection) so a failing password can no
  // longer be submitted blind (2026-07-07 incident).
  const [pwVerdict, setPwVerdict] = useState({ canSubmit: false, verdict: 'none' });

  const triggerShake = () => { setShaking(true); setTimeout(() => setShaking(false), 400); };

  const handleSet = async () => {
    // T-200 — per-field validation surfaces red borders + shake.
    let hasError = false;
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      setPwHasError(true);
      hasError = true;
    }
    if (!hasError && password !== confirmPw) {
      setError("Passwords do not match");
      setConfirmHasError(true);
      hasError = true;
    }
    if (hasError) { triggerShake(); return; }

    setLoading(true); setError("");
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) { setError(err.message); setLoading(false); triggerShake(); return; }
    setDone(true);
    setTimeout(() => onDone && onDone(), 1500);
  };

  const onPwChange = (e) => { setPassword(e.target.value); if (pwHasError) setPwHasError(false); };
  const onConfirmChange = (e) => { setConfirmPw(e.target.value); if (confirmHasError) setConfirmHasError(false); };

  const cardClassName = shaking ? `${styles.card} ${styles.cardShake}` : styles.card;
  const pwFieldClass = pwHasError ? `${styles.formField} ${styles.formFieldError}` : styles.formField;
  const confirmFieldClass = confirmHasError ? `${styles.formField} ${styles.formFieldError}` : styles.formField;

  return (
    <div className={styles.loginRoot}>
      <div className={cardClassName}>
        {/* Brand block — new DNA chrome (Brief C) */}
        <div className={styles.brand}>
          <span className={styles.brandLogo} aria-label="Carolina Lemke Berlin">
            <span className={styles.brandTop}>Carolina Lemke</span>
            <span className={styles.brandBottom}>Berlin</span>
          </span>
        </div>

        <div className={styles.brandDivider} aria-hidden="true"></div>
        <div className={styles.brandLine3}>Operations Hub</div>
        <div className={styles.separator}></div>

        {/* Greeting */}
        <div className={styles.welcome}>
          <h1 className={styles.welcomeTitle}>{title || "Set a new password"}</h1>
          {subtitle && <p className={styles.welcomeSub}>{subtitle}</p>}
        </div>

        {/* Success state */}
        {done ? (
          <div className={styles.resetSent}>
            <div className={styles.resetSentIcon}>
              <SetPasswordCheckIcon />
            </div>
            <p className={styles.resetSentMsg}>Password updated</p>
            <p className={styles.resetSentHint}>Redirecting…</p>
          </div>
        ) : (
          <div className={styles.form}>
            {/* New password */}
            <div className={pwFieldClass}>
              <div className={styles.fieldHead}>
                <label className={styles.fieldLabel} htmlFor="dana-new-pw">New password</label>
              </div>
              <div className={styles.fieldWrap}>
                <input
                  id="dana-new-pw"
                  type="password"
                  className={styles.fieldInput}
                  value={password}
                  onChange={onPwChange}
                  onKeyDown={e => e.key === "Enter" && handleSet()}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Live strength + will-it-pass verdict (server-policy mirror) */}
            <PasswordStrengthMeter
              password={password}
              confirm={confirmPw}
              onVerdict={setPwVerdict}
              onSuggest={(p) => { setPassword(p); setConfirmPw(p); setPwHasError(false); setConfirmHasError(false); }}
            />

            {/* Confirm password */}
            <div className={confirmFieldClass}>
              <div className={styles.fieldHead}>
                <label className={styles.fieldLabel} htmlFor="dana-confirm-pw">Confirm password</label>
              </div>
              <div className={styles.fieldWrap}>
                <input
                  id="dana-confirm-pw"
                  type="password"
                  className={styles.fieldInput}
                  value={confirmPw}
                  onChange={onConfirmChange}
                  onKeyDown={e => e.key === "Enter" && handleSet()}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
            </div>

            {error && <div className={styles.fieldError} role="alert">{error}</div>}

            <button
              type="button"
              className={`${styles.submit} ${loading ? styles.submitLoading : ''}`}
              onClick={handleSet}
              disabled={loading || !pwVerdict.canSubmit}
            >
              <span>{loading ? "Saving…" : "Set password"}</span>
            </button>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        © 2026 Carolina Lemke Berlin · Operations Hub v2.0 · Secure connection
      </div>
    </div>
  );
}
